// Reusable SidePanel editor for per-record Stammdaten visibility.
//
// Used by settings pages for Customers / Projects / Products / Services to
// configure which teams + users may see/edit a particular record. Default
// state ("Zugriff einschränken" OFF) leaves the record visible to everyone
// with the role permission — the toggle gates everything else.
//
// API contract:
//   GET  /api/access/:resourceType/:resourceId → { restricted, grants[] }
//   PUT  /api/access/:resourceType/:resourceId → replace-all
// Both wrapped by `api.getEntityAccess` / `api.setEntityAccess`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, UserSquare2, Users2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  api,
  type EntityAccess,
  type EntityGrant,
  type EntityPermission,
  type EntityResourceType,
} from '../lib/api'
import { notify } from '../lib/notify'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { Select } from './ui/select'
import { SidePanel } from './ui/side-panel'

interface EntityAccessPanelProps {
  open: boolean
  onClose: () => void
  resourceType: EntityResourceType
  /** Single-record mode: provide one id. Ignored when resourceIds is set. */
  resourceId?: string | null
  /** Bulk mode: apply the chosen restricted+grants to all listed ids. */
  resourceIds?: string[]
  /** Human label for the panel title. In bulk mode pass e.g. "5 Kunden". */
  resourceName: string
}

/** Resource-type → human label for the panel title. */
const RESOURCE_LABEL: Record<EntityResourceType, string> = {
  customer: 'Kunde',
  project: 'Projekt',
  product: 'Produkt',
  service: 'Tätigkeit',
  logbook_vehicle: 'Fahrzeug',
}

export function EntityAccessPanel({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceIds,
  resourceName,
}: EntityAccessPanelProps) {
  const queryClient = useQueryClient()
  const bulkMode = Array.isArray(resourceIds) && resourceIds.length > 0
  const accessQuery = useQuery({
    queryKey: ['entity-access', resourceType, resourceId],
    queryFn: () => api.getEntityAccess(resourceType, resourceId as string),
    enabled: open && !bulkMode && !!resourceId,
  })
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.listTeams(),
    enabled: open,
  })
  const usersQuery = useQuery({
    queryKey: ['users', 'access-picker'],
    queryFn: () => api.listUsers(false),
    enabled: open,
  })

  const [restricted, setRestricted] = useState(false)
  const [grants, setGrants] = useState<EntityGrant[]>([])

  // Hydrate local state when the server payload arrives or the panel re-opens.
  // In bulk mode there is no single source-of-truth row to read; start from
  // a blank slate so the admin's choice replaces every selected record.
  useEffect(() => {
    if (!open) return
    if (bulkMode) {
      setRestricted(false)
      setGrants([])
      return
    }
    if (accessQuery.data) {
      setRestricted(accessQuery.data.restricted)
      setGrants(accessQuery.data.grants)
    }
  }, [open, accessQuery.data, bulkMode])

  const allTeams = teamsQuery.data?.teams ?? []
  const allUsers = usersQuery.data?.users ?? []

  // Index granted subject IDs so the pickers can hide already-added rows.
  const grantedTeamIds = useMemo(
    () => new Set(grants.filter((g) => g.subjectType === 'team').map((g) => g.subjectId)),
    [grants],
  )
  const grantedUserIds = useMemo(
    () => new Set(grants.filter((g) => g.subjectType === 'user').map((g) => g.subjectId)),
    [grants],
  )

  const availableTeams = allTeams.filter((t) => !grantedTeamIds.has(t.id) && !t.archived)
  const availableUsers = allUsers.filter((u) => !grantedUserIds.has(u.id) && !u.archived)

  const save = useMutation({
    mutationFn: async (payload: EntityAccess) => {
      const slimGrants = payload.grants.map((g) => ({
        subjectType: g.subjectType,
        subjectId: g.subjectId,
        permission: g.permission,
      }))
      if (bulkMode) {
        return api.bulkSetEntityAccess(resourceType, {
          resourceIds: resourceIds as string[],
          restricted: payload.restricted,
          grants: slimGrants,
        })
      }
      if (!resourceId) throw new Error('resourceId fehlt')
      return api.setEntityAccess(resourceType, resourceId, {
        restricted: payload.restricted,
        grants: slimGrants,
      })
    },
    onSuccess: () => {
      // Invalidate the list query that owns the row, so the lock badge
      // refreshes immediately.
      const listKey =
        resourceType === 'customer'
          ? ['customers']
          : resourceType === 'project'
            ? ['projects']
            : resourceType === 'product'
              ? ['products']
              : ['services']
      queryClient.invalidateQueries({ queryKey: listKey })
      // Projekt-Hub hält Stammdaten zusätzlich unter ['project', id] —
      // flach invalidieren (deckt auch den Bulk-Mode ohne resourceId ab),
      // sonst zeigt der offene Hub-Kopf einen stalen Lock-Status.
      if (resourceType === 'project') {
        queryClient.invalidateQueries({ queryKey: ['project'] })
      }
      queryClient.invalidateQueries({
        queryKey: ['entity-access', resourceType, resourceId],
      })
      notify.success('Zugriff aktualisiert')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const addTeam = (teamId: string) => {
    if (!teamId) return
    const team = allTeams.find((t) => t.id === teamId)
    if (!team) return
    setGrants((g) => [
      ...g,
      { subjectType: 'team', subjectId: teamId, subjectName: team.name, permission: 'view' },
    ])
  }

  const addUser = (userId: string) => {
    if (!userId) return
    const user = allUsers.find((u) => u.id === userId)
    if (!user) return
    setGrants((g) => [
      ...g,
      { subjectType: 'user', subjectId: userId, subjectName: user.name, permission: 'view' },
    ])
  }

  const removeGrant = (subjectType: 'user' | 'team', subjectId: string) => {
    setGrants((g) => g.filter((x) => !(x.subjectType === subjectType && x.subjectId === subjectId)))
  }

  const setGrantPermission = (
    subjectType: 'user' | 'team',
    subjectId: string,
    permission: EntityPermission,
  ) => {
    setGrants((g) =>
      g.map((x) =>
        x.subjectType === subjectType && x.subjectId === subjectId ? { ...x, permission } : x,
      ),
    )
  }

  const teamGrants = grants.filter((g) => g.subjectType === 'team')
  const userGrants = grants.filter((g) => g.subjectType === 'user')

  const title = bulkMode
    ? `Zugriff: ${resourceName}`
    : `Zugriff: ${resourceName || RESOURCE_LABEL[resourceType]}`
  const description = bulkMode
    ? 'Diese Einstellung wird auf alle ausgewählten Datensätze angewendet.'
    : 'Wer darf diesen Datensatz sehen?'
  const saveDisabled = save.isPending || (!bulkMode && !resourceId)

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button
            onClick={() => save.mutate({ restricted, grants })}
            type="button"
            disabled={saveDisabled}
          >
            {bulkMode ? `Auf ${resourceIds?.length ?? 0} anwenden` : 'Speichern'}
          </Button>
        </>
      }
    >
      {!bulkMode && accessQuery.isLoading ? (
        <div className="px-2 py-6 text-sm text-text-secondary">Lade …</div>
      ) : (
        <div className="space-y-5 pb-2">
          {bulkMode && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
              Bestehende Freigaben der gewählten Datensätze werden durch diese Konfiguration
              ersetzt.
            </div>
          )}
          {/* Restriction toggle */}
          <label className="flex items-start gap-3 rounded-lg border border-border-subtle bg-bg-overlay/30 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={restricted}
              onChange={(e) => setRestricted(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-accent shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <Lock className="h-3.5 w-3.5 text-accent" /> Zugriff einschränken
              </div>
              <p className="mt-1 text-xs text-text-tertiary leading-relaxed">
                Aus: Jeder mit der jeweiligen Rollenberechtigung sieht diesen Datensatz. An: Nur
                Admins sowie ausdrücklich freigegebene Teams / Mitarbeiter haben Zugriff.
              </p>
            </div>
          </label>

          {restricted && (
            <>
              {/* Teams */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users2 className="h-4 w-4 text-text-secondary" />
                  <Label className="m-0">Teams</Label>
                </div>
                {teamGrants.length === 0 && (
                  <p className="text-xs text-text-tertiary px-1">Noch kein Team freigegeben.</p>
                )}
                {teamGrants.length > 0 && (
                  <ul className="space-y-1.5">
                    {teamGrants.map((g) => {
                      const teamName =
                        g.subjectName ?? allTeams.find((t) => t.id === g.subjectId)?.name ?? '—'
                      return (
                        <li
                          key={`team-${g.subjectId}`}
                          className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-overlay/20 px-2 py-1.5"
                        >
                          <span className="text-sm text-text-primary truncate flex-1">
                            {teamName}
                          </span>
                          <Select
                            value={g.permission}
                            onChange={(e) =>
                              setGrantPermission(
                                'team',
                                g.subjectId,
                                e.target.value as EntityPermission,
                              )
                            }
                            className="h-8 w-auto text-xs"
                          >
                            <option value="view">nur lesen</option>
                            <option value="edit">bearbeiten</option>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeGrant('team', g.subjectId)}
                            aria-label="Entfernen"
                            className="text-text-tertiary hover:text-danger"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      )
                    })}
                  </ul>
                )}
                <Select
                  value=""
                  onChange={(e) => addTeam(e.target.value)}
                  disabled={availableTeams.length === 0}
                  className="text-xs"
                >
                  <option value="">
                    {availableTeams.length === 0
                      ? '— alle Teams hinzugefügt —'
                      : '+ Team hinzufügen …'}
                  </option>
                  {availableTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Users */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserSquare2 className="h-4 w-4 text-text-secondary" />
                  <Label className="m-0">Mitarbeiter</Label>
                </div>
                {userGrants.length === 0 && (
                  <p className="text-xs text-text-tertiary px-1">
                    Noch kein Mitarbeiter freigegeben.
                  </p>
                )}
                {userGrants.length > 0 && (
                  <ul className="space-y-1.5">
                    {userGrants.map((g) => {
                      const userName =
                        g.subjectName ?? allUsers.find((u) => u.id === g.subjectId)?.name ?? '—'
                      return (
                        <li
                          key={`user-${g.subjectId}`}
                          className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-overlay/20 px-2 py-1.5"
                        >
                          <span className="text-sm text-text-primary truncate flex-1">
                            {userName}
                          </span>
                          <Select
                            value={g.permission}
                            onChange={(e) =>
                              setGrantPermission(
                                'user',
                                g.subjectId,
                                e.target.value as EntityPermission,
                              )
                            }
                            className="h-8 w-auto text-xs"
                          >
                            <option value="view">nur lesen</option>
                            <option value="edit">bearbeiten</option>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeGrant('user', g.subjectId)}
                            aria-label="Entfernen"
                            className="text-text-tertiary hover:text-danger"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      )
                    })}
                  </ul>
                )}
                <Select
                  value=""
                  onChange={(e) => addUser(e.target.value)}
                  disabled={availableUsers.length === 0}
                  className="text-xs"
                >
                  <option value="">
                    {availableUsers.length === 0
                      ? '— alle Mitarbeiter hinzugefügt —'
                      : '+ Mitarbeiter hinzufügen …'}
                  </option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Hint */}
              <p className="text-[11px] text-text-tertiary leading-relaxed px-1">
                Admins sehen und bearbeiten alle Datensätze, unabhängig von dieser Liste.
              </p>
            </>
          )}
        </div>
      )}
    </SidePanel>
  )
}
